import { useSearchParams } from "react-router-dom";
import { useTellescopeInfiniteQuery, type TellescopeModelFromName } from "@tellescope/tanstack-query";
import { buildFormAccessCodeRoute } from "../../config/portal";

type GroupOrdered = Pick<TellescopeModelFromName<"form_responses">, "groupPosition">;
type PushOrdered = Pick<TellescopeModelFromName<"form_responses">, "groupPosition" | "pushedToPortalAt" | "createdAt">;

/** Responses without a position sort last, so a malformed row never jumps ahead of the group. */
export const compareGroupPosition = (a: GroupOrdered, b: GroupOrdered): number =>
  (a.groupPosition ?? Number.MAX_SAFE_INTEGER) - (b.groupPosition ?? Number.MAX_SAFE_INTEGER);

// Legacy sorted by position and then re-sorted by push time, so push time is the primary key and
// position only orders forms pushed together.
const comparePushOrder = (a: PushOrdered, b: PushOrdered): number =>
  new Date(a.pushedToPortalAt ?? a.createdAt).getTime() - new Date(b.pushedToPortalAt ?? b.createdAt).getTime() ||
  compareGroupPosition(a, b);

// Fetched fresh on every visit and dropped on unmount: a cached list would send the patient back
// into a form they just submitted through the legacy iframe, which bypasses this query cache.
const FRESH_QUERY_OPTIONS = { staleTime: 0, gcTime: 0 };

export interface FormGroupEntryRedirect {
  isResolving: boolean;
  redirectTo: string | undefined;
}

/**
 * Resolves the legacy portal's `/documents` entry links to the form the patient should land on:
 *
 * - `?groupId=<id>[&groupInstance=<instance>]` — the first unsubmitted response of that form group
 *   send. `groupId` is matched as a plain string, so it covers both Form Group ids and the
 *   automation step ids that Push Forms to Portal stamps on its responses.
 * - `?onboarding=true` — the next form pushed to the portal, keeping `onboarding` on the form route so
 *   each submit returns here for the one after it.
 *
 * `redirectTo` stays undefined when nothing is left to fill out, so the Documents page renders.
 */
export const useFormGroupEntryRedirect = (pageSlug: string | undefined): FormGroupEntryRedirect => {
  const [searchParams] = useSearchParams();
  const isDocumentsPage = pageSlug === "documents";
  const groupId = (isDocumentsPage && searchParams.get("groupId")) || undefined;
  const groupInstance = searchParams.get("groupInstance") || undefined;
  const isOnboarding = isDocumentsPage && !groupId && searchParams.get("onboarding") === "true";

  const groupQuery = useTellescopeInfiniteQuery({
    model: "form_responses",
    method: "getSome",
    request: {
      filter: {
        groupId,
        ...(groupInstance ? { groupInstance } : {}),
        submittedAt: { _exists: false },
      },
    },
    select: (data) => data.pages.flat(),
    enabled: !!groupId,
    ...FRESH_QUERY_OPTIONS,
  });

  const onboardingQuery = useTellescopeInfiniteQuery({
    model: "form_responses",
    method: "getSome",
    request: {
      filter: {
        pushedToPortalAt: { _exists: true },
        submittedAt: { _exists: false },
      },
    },
    select: (data) => data.pages.flat(),
    enabled: isOnboarding,
    ...FRESH_QUERY_OPTIONS,
  });

  if (groupId) {
    const first = (groupQuery.data ?? []).filter((r) => !!r.accessCode).sort(compareGroupPosition)[0];
    return {
      isResolving: groupQuery.isLoading,
      redirectTo: first?.accessCode ? buildFormAccessCodeRoute(first.accessCode) : undefined,
    };
  }

  if (isOnboarding) {
    const next = (onboardingQuery.data ?? [])
      .filter((r) => !!r.accessCode && !r.hideFromEnduserPortal && !r.isInternalNote && r.source !== "Formsort")
      .sort(comparePushOrder)[0];
    return {
      isResolving: onboardingQuery.isLoading,
      redirectTo: next?.accessCode ? `${buildFormAccessCodeRoute(next.accessCode)}?onboarding=true` : undefined,
    };
  }

  return { isResolving: false, redirectTo: undefined };
};
